/**
 * ConflictMapPanel - Displays cross-workstream conflicts with severity and resolution.
 *
 * Each conflict is an expandable card showing the tension between two workstreams,
 * with severity badges and resolution details.
 */

import { useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { StrategicConflict } from '@/hooks/useStrategicStream';

const SEVERITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  critical:    { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  significant: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  minor:       { bg: 'bg-surface-light', text: 'text-text-secondary', border: 'border-border-subtle' },
};

interface ConflictMapPanelProps {
  conflicts: StrategicConflict[];
  className?: string;
}

export function ConflictMapPanel({ conflicts, className }: ConflictMapPanelProps) {
  const [expandedConflicts, setExpandedConflicts] = useState<Record<string, boolean>>({});

  const toggleConflict = useCallback((id: string) => {
    setExpandedConflicts(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  if (conflicts.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted text-sm">
        No cross-workstream conflicts were detected.
      </div>
    );
  }

  const criticalCount = conflicts.filter(c => c.severity === 'critical').length;
  const significantCount = conflicts.filter(c => c.severity === 'significant').length;
  const resolvedCount = conflicts.filter(c => c.resolved).length;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Summary bar */}
      <div className="flex items-center gap-3 text-xs text-text-muted">
        <span>{conflicts.length} conflicts detected</span>
        {criticalCount > 0 && (
          <span className="text-red-400">{criticalCount} critical</span>
        )}
        {significantCount > 0 && (
          <span className="text-amber-400">{significantCount} significant</span>
        )}
        <span className="text-green-400">{resolvedCount} resolved</span>
      </div>

      {/* Conflict cards */}
      {conflicts.map((conflict) => {
        const severity = SEVERITY_STYLES[conflict.severity] || SEVERITY_STYLES.minor;
        const isExpanded = expandedConflicts[conflict.conflictId] ?? false;

        return (
          <div
            key={conflict.conflictId}
            className={cn(
              'rounded-lg border bg-surface/50',
              severity.border,
            )}
          >
            {/* Header */}
            <button
              onClick={() => toggleConflict(conflict.conflictId)}
              aria-expanded={isExpanded}
              aria-controls={`conflict-content-${conflict.conflictId}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-light/30 transition-colors rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className={cn(
                  'px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex-shrink-0',
                  severity.bg, severity.text,
                )}>
                  {conflict.severity}
                </span>
                <span className="text-sm text-text-primary truncate">
                  {conflict.workstreamA} vs {conflict.workstreamB}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {conflict.resolved ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                )}
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-text-muted" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                )}
              </div>
            </button>

            {/* Description (always visible) */}
            <div className="px-4 pb-2 text-sm text-text-secondary">
              {conflict.description}
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div id={`conflict-content-${conflict.conflictId}`} role="region" className="px-4 pb-4 space-y-3">
                {/* Finding A */}
                {conflict.findingA && (
                  <div className="rounded-lg bg-surface-light/40 border border-border/20 p-3">
                    <div className="text-[10px] font-medium text-text-muted uppercase tracking-wide mb-1">
                      {conflict.workstreamA}
                    </div>
                    <div className="text-sm text-text-primary">
                      <MarkdownContent content={conflict.findingA} className="text-sm" />
                    </div>
                  </div>
                )}

                {/* Finding B */}
                {conflict.findingB && (
                  <div className="rounded-lg bg-surface-light/40 border border-border/20 p-3">
                    <div className="text-[10px] font-medium text-text-muted uppercase tracking-wide mb-1">
                      {conflict.workstreamB}
                    </div>
                    <div className="text-sm text-text-primary">
                      <MarkdownContent content={conflict.findingB} className="text-sm" />
                    </div>
                  </div>
                )}

                {/* Resolution */}
                {conflict.resolution && (
                  <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-3">
                    <div className="text-[10px] font-medium text-green-400/80 uppercase tracking-wide mb-1">
                      Resolution
                    </div>
                    <div className="text-sm text-text-primary">
                      <MarkdownContent content={conflict.resolution} className="text-sm" />
                    </div>
                    {conflict.resolutionRationale && (
                      <div className="mt-2 text-xs text-text-muted leading-relaxed">
                        <MarkdownContent content={conflict.resolutionRationale} className="text-xs" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
